import moment from "moment";
import momentTimezone from 'moment-timezone';
import React, { useContext, useState } from "react";
import { getFailedRefundReport, retryFailedRefund } from "../../../api/report";
import Confirmation from "Components/Modal/Confirmation/Confirmation";
import Header from "../../../Components/Header/Header";
import ReactTable from "../../../Components/Table/Table";
import { AppContext } from "../../../Context/AppContext";
import { MODULES, DEFAULT_TIMEZONE } from "./../../../Helpers/Constants";
import { getAccessByModuleName } from "./../../../Routes/Routes";
import { convertDateTime } from "Helpers/date";
import { Tooltip as ReactTooltip } from 'react-tooltip';
import GlobalDateFilters from "Components/GlobalDateFilters/GlobalDateFilters";

const REFUND_STATUS_LABELS = { 0: "Queued", 2: "Failed", 3: "Sent", 4: "Declined" };
// Queued and sent refunds may still reach the customer, so only failed and declined ones can be retried.
const RETRYABLE_STATUSES = [2, 4];

const FailedRefund = () => {
  const { showToastMessage, modules } = useContext(AppContext);
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [defaultFilter, setDefaultFilter] = React.useState({
    pageIndex: 0,
    pageSize: 10,
    filters: [],
    sortBy: [
      {
        id: "updatedAt",
        desc: "desc",
      },
    ],
  });
  let storeTimezone = localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE;
  let defaultStartDate = moment()
    .subtract(7, "days")
    .startOf("day")
    .format("ddd MMM DD, YYYY");
  let defaultEndDate = momentTimezone.tz(storeTimezone).startOf("day").format("ddd MMM DD, YYYY");
  const [fromDate, setFromDate] = useState(defaultStartDate);
  const [toDate, setToDate] = useState(defaultEndDate);

  const [pageCount, setPageCount] = React.useState(1);
  const [retryRow, setRetryRow] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const canRetry = getAccessByModuleName(modules, MODULES.EDIT_ORDER)?.permissions?.write_access;

  const handleRetry = async () => {
    setRetrying(true);
    const response = await retryFailedRefund(retryRow.id);
    setRetrying(false);
    setRetryRow(null);
    const succeeded = response?.status === 200;
    const message = response?.data?.message ?? "The retry request did not reach the server. Reload the report before you try again.";
    showToastMessage(undefined, message, succeeded ? "success" : "error", false);
    fetchData(defaultFilter);
  };

  const handleClearSearch = async (event) => {
    event.preventDefault();
    setFromDate(defaultStartDate);
    setToDate(defaultEndDate);
    fetchData({
      ...defaultFilter,
      fromDateDefault: defaultStartDate,
      toDateDefault: defaultEndDate,
    });
  };

  // Date Validation
  const dateValidation = (fromDate, toDate) => {
    let startDate = moment(fromDate).format("x");
    let endDate = moment(toDate).format("x");
    if (startDate > endDate) {
      showToastMessage(
        undefined,
        "Start date should not be greater than from end date",
        "error",
        false
      );
      return false;
    }
    return true;
  };

  const fetchData = async ({
    pageIndex,
    pageSize,
    filters,
    fromDateDefault,
    toDateDefault,
    sortBy,
  }) => {
    if (
      !dateValidation(
        fromDateDefault ? fromDateDefault : fromDate,
        toDateDefault ? toDateDefault : toDate
      )
    ) {
      return false;
    }
    let startDate = fromDateDefault ? fromDateDefault : fromDate;
    startDate = moment(startDate).format("YYYY-MM-DD");
    let endDate = toDateDefault ? toDateDefault : toDate;
    endDate = moment(endDate).format("YYYY-MM-DD");
    setDefaultFilter({ pageSize, pageIndex, filters });
    setLoading(true);
    const postData = {
      currentPage: pageIndex,
      itemsPerPage: pageSize,
      start_date: startDate,
      end_date: endDate,
      filters,
      sortBy,
    };

    let accountAccess = getAccessByModuleName(modules, MODULES.REPORTS);
    if (accountAccess && accountAccess.permissions.read_access) {
      const response = await getFailedRefundReport(postData);
      if (response && response.status === 200) {
        setData(response.data.data.rows);
        setPageCount(Math.ceil(response.data.data.count / pageSize));
      } else {
        showToastMessage(undefined, response?.data?.message, "error", false);
      }
    }
    setLoading(false);
    setDefaultFilter({ pageSize, pageIndex, filters, sortBy });
  };

  const columns = React.useMemo(() => [
    {
      Header: "Order Number",
      accessor: "order_number",
      isVisible: true,
      Cell: ({ value, row, column }) => {
        return `${row.original?.order?.order_number ?? ""}`;
      },
    },
    {
      Header: "Refund Amount",
      accessor: "total_amount",
      isVisible: true,
    },
    {
      Header: "Status",
      accessor: "refund_status",
      isVisible: true,
      disableFilters: true,
      disableSortBy: true,
      Cell: ({ value }) => REFUND_STATUS_LABELS[value] ?? "",
    },
    {
      Header: "Failure Reason",
      accessor: "description",
      isVisible: true,
      disableFilters: false,
      disableSortBy: true,
      Cell: ({ value, row, column }) => {
        const queuedNote = { 0: "Queued, never sent", 3: "Sent, no result from Payabli" }[row.original?.refund_status];
        if (queuedNote) return queuedNote;
        const tooltipId = `description_${row.original?.id}`;
        return row.original?.description?.length < 75 ? (
          `${row.original?.description ?? ""}`
        ) : (
          <div data-tooltip-id={tooltipId}>
            {row.original?.description.slice(0, 75) + "...."}
            <ReactTooltip
              id={tooltipId}
              place="top"
              arrowColor="#000"
              className="extraClass"
              positionStrategy="fixed"
              delayHide={1000}
              delayShow={500}
            >
              <p className="py-1 text-center">{row.original?.description}</p>
            </ReactTooltip>
          </div>
        );
      },
    },
    {
      Header: "Created On",
      accessor: "createdAt",
      colWidth: 280,
      isVisible: true,
      disableFilters: true,
      disableSortBy: false,
      Cell: ({ value, row, column }) => {
        return `${
          row.original.createdAt && row.original.createdAt !== null
            ? convertDateTime(row.original.createdAt, "iso", "ccc LLL dd, yyyy, hh:mm a")
            : ""
        }`;
      },
    },
    {
      Header: "Failed On",
      accessor: "updatedAt",
      colWidth: 280,
      isVisible: true,
      disableFilters: true,
      Cell: ({ value, row, column }) => {
        return `${
          row.original.updatedAt && row.original.updatedAt !== null
            ? convertDateTime(row.original.updatedAt, "iso", "ccc LLL dd, yyyy, hh:mm a")
            : ""
        }`;
      },
    },
    ...(canRetry
      ? [
          {
            Header: "Action",
            accessor: "id",
            isVisible: true,
            disableFilters: true,
            disableSortBy: true,
            Cell: ({ row }) => RETRYABLE_STATUSES.includes(row.original.refund_status) && (
              <button
                type="button"
                className="btn-sm outline-btn cursor-pointer"
                disabled={retrying}
                onClick={() => setRetryRow(row.original)}
              >
                Retry
              </button>
            ),
          },
        ]
      : []),
  ]);

  return (
    <div>
      <Header title="Failed Refund" />
      {retryRow && (
        <Confirmation
          showModal
          title="Retry refund"
          message={`Retry the $${retryRow.total_amount} refund for order ${retryRow.order?.order_number ?? ""}?`}
          btn1_title={retrying ? "Retrying..." : "Retry"}
          btn2_title="Cancel"
          btn1_type="button"
          btn2_type="button"
          handleSubmitClick={() => !retrying && handleRetry()}
          handleCancelClick={() => !retrying && setRetryRow(null)}
        />
      )}
      <div className="row mb-5">
        <div className="col-sm-12 col-xs-12 m-0 p-0">
          <GlobalDateFilters
            isDateFilter
            isSearchIcon
            isClearSearchBtn
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
            fetchData={()=>fetchData({
              ...defaultFilter
            })}
            handleClearSearch={handleClearSearch}
            key={"failed-refund"}
          />
        </div>
      </div>
      <div>
        <ReactTable
          columns={columns}
          data={data}
          loading={loading}
          fetchData={fetchData}
          pageCount={pageCount}
          defaultFilter={defaultFilter}
          noDataText={"There is no data to display"}
          displayGlobalSearch={false}
          displayColumnVisibility={false}
          displayLastColumnSorting={true}
        />
      </div>
    </div>
  );
};
export default FailedRefund;
